/**
 * Briscola bot.
 *
 * Plays from the bot's own view only:
 *  - Leading: throw the cheapest non-trump, zero-point card; keep trumps and point cards back.
 *  - Partner already winning: load the trick with points ("carico") once it is safe, otherwise play low.
 *  - Opponent winning: take a trick that is worth it with the cheapest card that wins (a plain card before a trump),
 *    otherwise give up the cheapest card.
 *  - HARD/EXPERT/NORMAL count the cards played so far to tell whether the partner's card can still be beaten.
 */
import { parseCard, type CardId, type Suit } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import {
  briscolaDeck,
  briscolaPartnerOf,
  briscolaPoints,
  briscolaStrength,
  briscolaTrickWinner,
  type BriscolaAction,
  type BriscolaLegal,
  type BriscolaTrickPlay,
  type BriscolaView,
} from './engine.js';

const suitOf = (id: CardId) => parseCard(id).suit;

/** How much it hurts to give a card away: plain zero-point cards are cheapest, trumps and point cards dearest. */
const cost = (c: CardId, trump: Suit): number => (suitOf(c) === trump ? 1000 : 0) + briscolaPoints(c) * 20 + briscolaStrength(c);
const cheapest = (cards: readonly CardId[], trump: Suit): CardId => [...cards].sort((a, b) => cost(a, trump) - cost(b, trump))[0]!;

function unseenCards(view: BriscolaView, ctx: BotContext): Set<CardId> {
  const seen = new Set<CardId>(view.hand);
  view.trick.forEach((p) => seen.add(p.card));
  if (view.trumpCardInStock) seen.add(view.trumpCard);
  const n = view.tricks.length;
  const keep = ctx.profile.memory >= 1 ? n : Math.round(n * ctx.profile.memory);
  view.tricks.forEach((t, i) => {
    if (i >= n - keep) for (const p of t.plays) seen.add(p.card);
  });
  return new Set(briscolaDeck().filter((c) => !seen.has(c)));
}

function wouldWin(trick: readonly BriscolaTrickPlay[], seat: number, card: CardId, trump: Suit): boolean {
  if (trick.length === 0) return true;
  return briscolaTrickWinner([...trick, { seat, card }], trump) === seat;
}

/** True when no unseen card could still beat `card`. */
function isUnbeatable(card: CardId, trump: Suit, unseen: Set<CardId>): boolean {
  for (const u of unseen) {
    const s = suitOf(u);
    if (suitOf(card) === trump) {
      if (s === trump && briscolaStrength(u) > briscolaStrength(card)) return false;
    } else if (s === trump || (s === suitOf(card) && briscolaStrength(u) > briscolaStrength(card))) {
      return false;
    }
  }
  return true;
}

function lead(view: BriscolaView, legal: CardId[]): CardId {
  const trump = view.trump;
  const plain = legal.filter((c) => suitOf(c) !== trump && briscolaPoints(c) === 0);
  if (plain.length) return cheapest(plain, trump);
  // Nothing free to throw: lose as little as possible, but a lone trump with an empty stock is a strong lead.
  if (view.stockCount === 0) {
    const trumps = legal.filter((c) => suitOf(c) === trump).sort((a, b) => briscolaStrength(b) - briscolaStrength(a));
    if (trumps.length) return trumps[0]!;
  }
  return cheapest(legal, trump);
}

function follow(view: BriscolaView, legal: CardId[], ctx: BotContext): CardId {
  const me = view.seat!;
  const trump = view.trump;
  const trick = view.trick;
  const partner = briscolaPartnerOf(me);
  const winnerSeat = briscolaTrickWinner(trick, trump);
  const winnerCard = trick.find((p) => p.seat === winnerSeat)!.card;
  const onTable = trick.reduce((n, p) => n + briscolaPoints(p.card), 0);
  const last = trick.length === 3;
  const unseen = ctx.profile.memory > 0 ? unseenCards(view, ctx) : null;
  const winners = legal.filter((c) => wouldWin(trick, me, c, trump));
  const plainWinners = winners.filter((c) => suitOf(c) !== trump);

  if (winnerSeat === partner) {
    const safe = last || (unseen ? isUnbeatable(winnerCard, trump, unseen) : briscolaStrength(winnerCard) >= 9 || suitOf(winnerCard) === trump);
    if (safe) {
      // Load the trick with points, keeping trumps.
      const loads = legal.filter((c) => suitOf(c) !== trump && briscolaPoints(c) > 0);
      if (loads.length) return [...loads].sort((a, b) => briscolaPoints(b) - briscolaPoints(a))[0]!;
    }
    return cheapest(legal, trump);
  }

  // An opponent has the trick: take it when it carries enough points, spending a trump only on a rich trick.
  if (winners.length > 0) {
    const worth = onTable >= 10 || (last && onTable > 0) || (onTable >= 4 && suitOf(winnerCard) !== trump) || (view.stockCount === 0 && onTable > 0);
    if (worth) {
      if (plainWinners.length) return cheapest(plainWinners, trump);
      if (onTable >= 10 || last) return cheapest(winners, trump);
    }
  }
  return cheapest(legal, trump);
}

export function briscolaBotChoose(view: BriscolaView, legal: CardId[], ctx: BotContext): CardId {
  if (legal.length === 1) return legal[0]!;
  return view.trick.length === 0 ? lead(view, legal) : follow(view, legal, ctx);
}

export const briscolaBot: BotEngine<BriscolaView, BriscolaLegal, BriscolaAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) return { type: 'play', card: cards[0]! };
    if (chance(ctx, ctx.profile.mistakeRate)) return { type: 'play', card: cards[ctx.rng.int(cards.length)]! };
    const card = briscolaBotChoose(view, cards, ctx);
    return { type: 'play', card: cards.includes(card) ? card : cards[0]! };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = me % 2;
    const diff = view.teamPoints[team]! - view.teamPoints[1 - team]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 120));
  },

  calculateRisk() {
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'play') return { type: 'play', card: cheapest(legal.cards, view.trump) };
    throw new Error('No legal action');
  },
};
